import { AccessGrant, Door, Location, Query, Visitor, VisitorGroup } from 'graphql/generated/graphqlTypes';
import { generateMockAccessGrant } from 'mocks/graphqlMocks/accessGrant';
import { removeFields } from 'mocks/graphqlMocks/removeFields';
import { mockVisitorsInstallersAndCleaners } from 'mocks/graphqlMocks/visitorFixtures';
import { generateMockVisitorGroup } from 'mocks/graphqlMocks/visitorGroup';
import { mockedGetAllVisitorsGroupsWithoutNestedResolversDataForQuery } from './getAllVisitorsGroupsWithoutNestedResolvers';
import { mockedGetDoorsForTenantWithLocationsDataAdmin } from './getDoorsForTenantWithLocations';
import {
  mockedGetLocationWithDoorsForTenantDataAdmin,
  mockedGetLocationWithDoorsForTenantDataUnitTests
} from './getLocationWithDoorsForTenant';

export function mockedGetVisitorGroupsForTenantWithAccessGrantsData(
  getVisitorGroupsForTenant: Array<VisitorGroup>
): Pick<Query, 'getVisitorGroupsForTenant'> {
  return {
    getVisitorGroupsForTenant
  };
}

// GET_VISITOR_GROUPS_FOR_TENANT_WITH_ACCESS_GRANTS only selects id/name/accessGrants/externalRef/type
const fieldsToRemoveFromVisitorGroups: (keyof VisitorGroup)[] = [
  'allowedAccessGrants',
  'defaultAccessGrants',
  'isAdminGroup',
  'isQrCodeVisitInviterGroup',
  'maxNumberOfMembers',
  'memberIds',
  'members',
  'scheduledAccessGrants',
  'tenantId'
];
// accessGrants only selects id/accessSchedule/doors/locations
const fieldsToRemoveFromAccessGrants: (keyof AccessGrant)[] = [
  'doorIds',
  'isOfficeModeEnabled',
  'locationIds',
  'name',
  'tenantId'
];
// accessGrants.locations.doors only selects id/name/hardwareIdentifier/batteryWarningLevel
const fieldsToRemoveFromLocationDoors: (keyof Door)[] = ['type'];

const withTrimmedDoors = (location: Location): Location => ({
  ...location,
  doors: location.doors.map((door) => removeFields(door, fieldsToRemoveFromLocationDoors))
});

export const mockedGetVisitorGroupsForTenantWithAccessGrantsDataDataForQuery =
  mockedGetVisitorGroupsForTenantWithAccessGrantsData([
    removeFields(
      generateMockVisitorGroup({
        id: 'mockedVisitorGroupId0',
        accessGrants: [
          removeFields(
            generateMockAccessGrant({
              id: 'mockedAccessGrantId0',
              locations: [withTrimmedDoors(mockedGetLocationWithDoorsForTenantDataUnitTests.getLocationsForTenant[0])]
            }),
            fieldsToRemoveFromAccessGrants
          )
        ],
        name: 'group 0'
      }),
      fieldsToRemoveFromVisitorGroups
    ),
    removeFields(
      generateMockVisitorGroup({
        id: 'mockedVisitorGroupId1',
        accessGrants: [
          removeFields(
            generateMockAccessGrant({
              id: 'mockedAccessGrantId1',
              locations: [withTrimmedDoors(mockedGetLocationWithDoorsForTenantDataUnitTests.getLocationsForTenant[1])]
            }),
            fieldsToRemoveFromAccessGrants
          )
        ],
        name: 'group 1'
      }),
      fieldsToRemoveFromVisitorGroups
    ),
    removeFields(
      generateMockVisitorGroup({
        id: 'mockedVisitorGroupId2',
        accessGrants: [
          removeFields(
            generateMockAccessGrant({
              id: 'mockedAccessGrantId2',
              locations: [
                withTrimmedDoors(mockedGetLocationWithDoorsForTenantDataUnitTests.getLocationsForTenant[1]),
                withTrimmedDoors(mockedGetLocationWithDoorsForTenantDataUnitTests.getLocationsForTenant[2])
              ]
            }),
            fieldsToRemoveFromAccessGrants
          )
        ],
        name: 'group 2'
      }),
      fieldsToRemoveFromVisitorGroups
    )
  ]);

// Derived from the same fixture the "without nested resolvers" warm-up query uses (memberIds/locationIds/doorIds),
// resolved into full nested objects — keeps this query's mock and the warm-up mock's counts in sync by construction.
const visitorsById = new Map(mockVisitorsInstallersAndCleaners.map((visitor) => [visitor.id, visitor]));
const locationsById = new Map(
  mockedGetLocationWithDoorsForTenantDataAdmin.getLocationsForTenant.map((location) => [location.id, location])
);
const doorsById = new Map(
  mockedGetDoorsForTenantWithLocationsDataAdmin.getDoorsForTenant.map((door) => [door.id, door])
);

export const mockedGetVisitorGroupsForTenantWithAccessGrantsDataAdmin =
  mockedGetVisitorGroupsForTenantWithAccessGrantsData(
    mockedGetAllVisitorsGroupsWithoutNestedResolversDataForQuery.getVisitorGroupsForTenant.map((group) => ({
      ...group,
      members: group.memberIds
        .map((memberId) => visitorsById.get(memberId))
        .filter((visitor): visitor is Visitor => Boolean(visitor)),
      accessGrants: group.accessGrants.map((accessGrant) => ({
        ...accessGrant,
        locations: accessGrant.locationIds
          .map((locationId) => locationsById.get(locationId))
          .filter((location): location is Location => Boolean(location)),
        doors: accessGrant.doorIds.map((doorId) => doorsById.get(doorId)).filter((door): door is Door => Boolean(door))
      }))
    }))
  );
